import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useLocationSearchV2 } from '../../hooks/useLocationSearchV2';
import { useUserLocation } from '../../hooks/useUserLocation';
import LocationSuggestionsV2 from './LocationSuggestionsV2';
import '../../styles/location-search-v2.css';

/**
 * LocationSearchFieldV2 - Main location search input component
 * Provides city-level location search with autocomplete functionality
 */
const LocationSearchFieldV2 = ({
  value = '',
  onChange,
  onLocationSelect,
  placeholder = 'Search for a city...',
  disabled = false,
  className = '',
  inputClassName = '',
  error = null,
  label = 'Home City',
  required = false,
  debounceMs = 300,
  minQueryLength = 2,
  autoSaveToProfile = false
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [isFocused, setIsFocused] = useState(false);

  const inputRef = useRef(null);
  const containerRef = useRef(null);

  // Use the location search hook
  const {
    suggestions,
    loading,
    error: searchError,
    searchLocations,
    clearSuggestions,
    clearError,
    getLocationDetails,
    hasResults,
    isEmpty
  } = useLocationSearchV2({ debounceMs, minQueryLength });

  // Use the user location hook for auto-saving
  const {
    updateLocation,
    loading: locationUpdateLoading,
    error: locationUpdateError,
    clearError: clearLocationError
  } = useUserLocation();

  // Update input value when prop changes
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Handle clicks outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowSuggestions(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  /**
   * Handle input value changes
   */
  const handleInputChange = useCallback((event) => {
    const newValue = event.target.value;
    setInputValue(newValue);
    setSelectedIndex(-1);
    
    // Clear any previous errors
    if (searchError) {
      clearError();
    }

    // Trigger search
    searchLocations(newValue);
    
    // Show suggestions if we have input
    setShowSuggestions(newValue.length >= minQueryLength);

    // Notify parent of input change
    if (onChange) {
      onChange(newValue);
    }
  }, [onChange, searchLocations, clearError, searchError, minQueryLength]);

  /**
   * Handle input focus
   */
  const handleInputFocus = useCallback(() => {
    setIsFocused(true);
    
    // Show suggestions if we have results or are loading
    if (inputValue.length >= minQueryLength && (hasResults || loading)) {
      setShowSuggestions(true);
    }
  }, [inputValue, minQueryLength, hasResults, loading]);

  /**
   * Handle input blur
   */
  const handleInputBlur = useCallback(() => {
    setIsFocused(false);
    // Don't immediately hide suggestions to allow for clicks
    setTimeout(() => {
      setShowSuggestions(false);
      setSelectedIndex(-1);
    }, 200);
  }, []);

  /**
   * Handle keyboard navigation
   */
  const handleKeyDown = useCallback((event) => {
    if (!showSuggestions || suggestions.length === 0) {
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : 0
        );
        break;

      case 'ArrowUp':
        event.preventDefault();
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : suggestions.length - 1
        );
        break;

      case 'Enter':
        event.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSuggestionSelect(suggestions[selectedIndex], selectedIndex);
        }
        break;

      case 'Escape':
        event.preventDefault();
        setShowSuggestions(false);
        setSelectedIndex(-1);
        inputRef.current?.blur();
        break;

      case 'Tab':
        // Allow tab to close suggestions
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;

      default:
        break;
    }
  }, [showSuggestions, suggestions, selectedIndex]);

  /**
   * Handle suggestion selection
   */
  const handleSuggestionSelect = useCallback(async (suggestion, index) => {
    try {
      // Get detailed location information
      const locationDetails = await getLocationDetails(suggestion.id);
      
      // Update input with full location display (city, state, country)
      const fullLocationText = [
        locationDetails.city,
        locationDetails.state,
        locationDetails.country
      ].filter(Boolean).join(', ');
      
      setInputValue(fullLocationText);
      setShowSuggestions(false);
      setSelectedIndex(-1);

      // Auto-save to user profile if enabled
      if (autoSaveToProfile) {
        try {
          const success = await updateLocation(locationDetails);
          if (success) {
            console.log('Location saved to user profile successfully');
          }
        } catch (saveError) {
          console.error('Error saving location to profile:', saveError);
          // Don't block the UI flow for save errors
        }
      }

      // Notify parent components with full location text
      if (onChange) {
        onChange(fullLocationText);
      }
      
      if (onLocationSelect) {
        onLocationSelect(locationDetails, suggestion);
      }

      // Blur the input to remove focus after selection
      inputRef.current?.blur();
    } catch (err) {
      console.error('Error selecting location:', err);
      // Error is already handled by the hook
    }
  }, [getLocationDetails, onChange, onLocationSelect, autoSaveToProfile, updateLocation]);

  /**
   * Handle keyboard navigation from suggestions
   */
  const handleKeyboardNavigation = useCallback((index) => {
    setSelectedIndex(index);
  }, []);

  /**
   * Clear the input and suggestions
   */
  const handleClear = useCallback(() => {
    setInputValue('');
    setShowSuggestions(false);
    setSelectedIndex(-1);
    clearSuggestions();
    
    if (onChange) {
      onChange('');
    }
    
    inputRef.current?.focus();
  }, [onChange, clearSuggestions]);

  // Determine if we should show suggestions
  const shouldShowSuggestions = showSuggestions && (hasResults || loading || isEmpty);

  // Combine errors
  const displayError = error || searchError || locationUpdateError;

  return (
    <div 
      ref={containerRef}
      className={`location-search-field-v2 ${className}`}
    >
      {/* Label */}
      {label && (
        <label
          htmlFor="location-search-v2"
          className="location-label"
        >
          {label}
          {required && (
            <span className="required">*</span>
          )}
        </label>
      )}

      {/* Input container */}
      <div className="location-input-container">
        <input
          ref={inputRef}
          id="location-search-v2"
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className={`location-input ${inputClassName} ${displayError ? 'error' : ''}`}
          autoComplete="off"
          role="combobox"
          aria-expanded={shouldShowSuggestions}
          aria-haspopup="listbox"
          aria-autocomplete="list"
          aria-describedby={displayError ? 'location-search-v2-error' : undefined}
        />

        {/* Clear button */}
        {inputValue && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            className="location-clear-btn"
            aria-label="Clear location"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        )}

        {/* Loading indicator */}
        {(loading || locationUpdateLoading) && (
          <div className={`location-loading ${inputValue ? 'with-clear' : ''}`} />
        )}
      </div>

      {/* Suggestions dropdown */}
      <LocationSuggestionsV2
        suggestions={suggestions}
        onSelect={handleSuggestionSelect}
        loading={loading}
        visible={shouldShowSuggestions}
        selectedIndex={selectedIndex}
        onKeyboardNavigation={handleKeyboardNavigation}
      />

      {/* Error message */}
      {displayError && (
        <div
          id="location-search-v2-error"
          className="location-error"
        >
          {displayError}
        </div>
      )}
    </div>
  );
};

LocationSearchFieldV2.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  onLocationSelect: PropTypes.func,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  inputClassName: PropTypes.string,
  error: PropTypes.string,
  label: PropTypes.string,
  required: PropTypes.bool,
  debounceMs: PropTypes.number,
  minQueryLength: PropTypes.number,
  autoSaveToProfile: PropTypes.bool
};

export default LocationSearchFieldV2;